// Instrumentación de CLIENTE (Next 16, v15.3+). Corre en el browser después
// de cargar el HTML y ANTES de la hidratación de React, así que captura
// errores tempranos también. Acá viven los handlers globales que los error
// boundaries de React NO pueden ver: errores en event handlers, código async,
// promesas no manejadas y llamadas a console.error.
//
// Todo va envuelto en try/catch: una falla del tracking nunca puede degradar
// la app (recomendación de la doc de instrumentation-client).

import { reportClientError } from "@/core/observability/client-report";

function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

// Evita capturar nuestros propios logs internos (anti-loop).
function isInternalLog(message: string): boolean {
  return (
    message.startsWith("[logAppError]") ||
    message.startsWith("[onRequestError]")
  );
}

try {
  // 1. Errores no atrapados (window.onerror). Cubre throws en event handlers
  //    y errores de scripts. Ignora errores de carga de recursos (img/script
  //    sin .error), que no son excepciones de la app.
  window.addEventListener("error", (event: ErrorEvent) => {
    if (event.error instanceof Error) {
      reportClientError({ error: event.error, context: "window.onerror" });
    } else if (event.message) {
      reportClientError({ error: event.message, context: "window.onerror" });
    }
  });

  // 2. Promesas rechazadas sin catch (unhandledrejection).
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      reportClientError({
        error:
          reason instanceof Error
            ? reason
            : `Unhandled rejection: ${stringifyArg(reason)}`,
        context: "unhandledrejection",
      });
    },
  );

  // 3. console.error. Sólo capturamos llamadas SIN un Error como argumento:
  //    los Error ya los capturan los handlers de arriba y los error
  //    boundaries con mejor contexto. Esto rescata los console.error de
  //    "algo falló" que hoy no dejan ningún rastro. severity 'warning'
  //    porque es menor señal que una excepción.
  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const hasErrorArg = args.some((a) => a instanceof Error);
      if (!hasErrorArg) {
        const message = args.map(stringifyArg).join(" ").slice(0, 4000);
        if (message && !isInternalLog(message)) {
          reportClientError({
            error: message,
            context: "console.error",
            severity: "warning",
          });
        }
      }
    } catch {
      // nunca romper el console.error original
    }
    originalConsoleError(...args);
  };
} catch {
  // Si la instrumentación no se pudo instalar, la app sigue funcionando.
}
